import type { CSSProperties } from 'react';
import { motion, type Variants } from 'framer-motion';
import { PLAN_DESCRIPTIONS } from '@/features/subscription/data/planDescriptions';
import { formatPrice } from '@/features/subscription/components/CardPaymentForm';

const PLAN_DISCOUNTS: Record<string, { originalPriceCents: number; discount: string }> = {
  GOURMET: { originalPriceCents: 12000, discount: '33%' },
  BANQUET: { originalPriceCents: 30000, discount: '33%' },
};

const containerVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.1 } },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } },
};

export interface PlanForList {
  slug: string;
  name: string;
  valueCents: number;
  comingSoon?: boolean | null;
  effectiveValueCents?: number | null;
  discountLabel?: string | null;
}

interface PlanListStepProps {
  plans: readonly PlanForList[];
  currentPlan: string | null;
  isLoading: boolean;
  error: string | null;
  onPlanClick: (args: { planSlug: string; planName: string; valueCents: number; isFree: boolean }) => void;
}

// Base alinhado ao `.button` (rounded-full px-5 py-2.5 text-sm) pra os 3 estados
// renderizarem com mesmas dimensões — só muda cor/tratamento.
const planButtonClass = ({ isCurrent, comingSoon, isFree }: { isCurrent: boolean; comingSoon: boolean; isFree: boolean }): string => {
  const base = 'w-full rounded-full px-5 py-2.5 text-sm font-semibold transition flex justify-center';
  if (isCurrent) return `${base} cursor-default`;
  if (comingSoon) return `${base} bg-slate-100 text-slate-400 cursor-not-allowed`;
  if (isFree) return `${base} bg-slate-100 text-slate-700 hover:bg-slate-200`;
  return `${base} button`;
};

// O ring marca o plano ATUAL do tenant (não mais o "Mais popular", que continua
// indicado pelo badge no topo do card).
const planCardClass = ({ isCurrent, comingSoon }: { isCurrent: boolean; comingSoon: boolean }): string => {
  const highlight = isCurrent ? 'ring-2 ring-primary' : '';
  const dimmed = comingSoon ? 'opacity-60' : '';
  return `card flex flex-col p-5 space-y-4 ${highlight} ${dimmed}`.trim();
};

const planButtonStyle = ({ isCurrent }: { isCurrent: boolean }): CSSProperties | undefined =>
  isCurrent ? { backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary-strong)' } : undefined;

const planButtonLabel = ({ isLoading, isCurrent, comingSoon, isFree }: { isLoading: boolean; isCurrent: boolean; comingSoon: boolean; isFree: boolean }): string => {
  if (isLoading) return 'Aguarde...';
  if (isCurrent) return 'Plano atual';
  if (comingSoon) return 'Em breve';
  if (isFree) return 'Continuar grátis';
  return 'Assinar';
};

export function PlanListStep({ plans, currentPlan, isLoading, error, onPlanClick }: PlanListStepProps) {
  return (
    <>
      <div className="mb-6 pr-10">
        <h2 className="font-display text-xl font-bold text-slate-900">Atualizar plano</h2>
        <p className="mt-1 text-sm text-slate-600">Faça upgrade ou mude de plano quando quiser.</p>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 text-center">
          {error}
        </div>
      )}

      <motion.div
        key="plan-grid"
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 py-2"
      >
        {plans.map((plan) => {
          const desc = PLAN_DESCRIPTIONS[plan.slug];
          const isCurrent = currentPlan === plan.slug;
          const isFree = plan.valueCents === 0;
          const comingSoon = plan.comingSoon ?? false;
          const discountInfo = PLAN_DISCOUNTS[plan.slug];
          const hasPromoDiscount = plan.effectiveValueCents != null && plan.discountLabel;
          const hasAnyDiscount = hasPromoDiscount || !!discountInfo;

          const getPriceMargin = () => hasAnyDiscount ? 'mt-0.5' : 'mt-2';
          const getDisplayPrice = () => hasPromoDiscount
            ? formatPrice({ valueCents: plan.effectiveValueCents! })
            : formatPrice({ valueCents: plan.valueCents });

          return (
            <motion.div
              key={plan.slug}
              variants={cardVariants}
              className={planCardClass({ isCurrent, comingSoon })}
            >
              {plan.slug === 'BANQUET' && (
                <span className="inline-block rounded-full px-3 py-1 text-xs font-semibold self-start" style={{ backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary-strong)' }}>
                  Mais popular
                </span>
              )}
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">{plan.name}</h3>
                <p className="mt-1 text-xs text-slate-500">{desc?.description}</p>
                {hasPromoDiscount ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-slate-400 line-through">
                      {formatPrice({ valueCents: plan.valueCents })}
                    </span>
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary)' }}>
                      {plan.discountLabel}
                    </span>
                  </div>
                ) : discountInfo ? (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-sm text-slate-400 line-through">
                      {formatPrice({ valueCents: discountInfo.originalPriceCents })}
                    </span>
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: 'var(--color-primary-soft)', color: 'var(--color-primary)' }}>
                      -{discountInfo.discount} OFF
                    </span>
                  </div>
                ) : null}
                <p className={`${getPriceMargin()} text-xl font-bold text-slate-900`}>
                  {getDisplayPrice()}
                </p>
              </div>

              <ul className="space-y-1.5 flex-1">
                {desc?.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-xs text-slate-600">
                    <svg viewBox="0 0 20 20" className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-primary" fill="currentColor">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => !comingSoon && !isCurrent && onPlanClick({
                  planSlug: plan.slug,
                  planName: plan.name,
                  valueCents: plan.valueCents,
                  isFree,
                })}
                disabled={comingSoon || isCurrent || isLoading}
                className={planButtonClass({ isCurrent, comingSoon, isFree })}
                style={planButtonStyle({ isCurrent })}
              >
                {planButtonLabel({ isLoading, isCurrent, comingSoon, isFree })}
              </button>
            </motion.div>
          );
        })}
      </motion.div>

    </>
  );
}
